/* eslint-disable no-unused-vars */
import * as THREE from 'three';

/**
 * Extracts filtered edges from a mesh by removing edges present in the 90-degree EdgesGeometry (external border)
 * @param {THREE.Mesh} mesh - Input mesh
 * @param {number} filterAngle - Angle threshold for internal edges (e.g., 5 degrees)
 * @returns {THREE.BufferGeometry} Filtered edges geometry
 */
function getFilteredEdgesGeometry(mesh, filterAngle = 5) {
  // Generate EdgesGeometry with filterAngle (internal edges)
  const edgesGeometryFiltered = new THREE.EdgesGeometry(mesh.geometry, filterAngle);
  // Generate EdgesGeometry with 90 degrees (external border edges)
  const edgesGeometry90 = new THREE.EdgesGeometry(mesh.geometry, 90);

  // Extract edges as sets of vertex pairs from EdgesGeometry
  function extractEdgesSet(geometry) {
    const positions = geometry.attributes.position.array;
    const edges = new Set();
    for (let i = 0; i < positions.length; i += 6) {
      const v1 = `${positions[i].toFixed(5)},${positions[i + 1].toFixed(5)},${positions[i + 2].toFixed(5)}`;
      const v2 = `${positions[i + 3].toFixed(5)},${positions[i + 4].toFixed(5)},${positions[i + 5].toFixed(5)}`;
      const edgeKey = v1 < v2 ? `${v1}|${v2}` : `${v2}|${v1}`;
      edges.add(edgeKey);
    }
    return edges;
  }

  const edgesFilteredSet = extractEdgesSet(edgesGeometryFiltered);
  const edges90Set = extractEdgesSet(edgesGeometry90);

  // Filter edgesFilteredSet to exclude any edges present in edges90Set
  const filteredEdges = [];
  edgesFilteredSet.forEach(edge => {
    if (!edges90Set.has(edge)) {
      filteredEdges.push(edge);
    }
  });

  // Convert filtered edges back to Float32Array for BufferGeometry
  const filteredPositions = new Float32Array(filteredEdges.length * 6);
  filteredEdges.forEach((edge, idx) => {
    const [v1, v2] = edge.split('|');
    const v1Coords = v1.split(',').map(Number);
    const v2Coords = v2.split(',').map(Number);
    filteredPositions.set(v1Coords, idx * 6);
    filteredPositions.set(v2Coords, idx * 6 + 3);
  });

  const filteredGeometry = new THREE.BufferGeometry();
  filteredGeometry.setAttribute('position', new THREE.BufferAttribute(filteredPositions, 3));

  return filteredGeometry;
}

/**
 * For each tooth in dentalMap, collects vertices from filteredEdgesGeometry within a given radius around tooth center
 * @param {THREE.BufferGeometry} filteredEdgesGeometry - Filtered edges geometry
 * @param {Object} dentalMap - Dental mapping data with medial and distal points per tooth
 * @param {number} radius - Radius around tooth center to collect vertices
 * @returns {Object} Object with toothId keys and array of THREE.Vector3 vertices as values
 */
function getVerticesPerTooth(filteredEdgesGeometry, dentalMap, radius) {
  const positions = filteredEdgesGeometry.attributes.position.array;
  const vertexCount = positions.length / 3;

  // Extract unique vertices from edges geometry
  const uniqueVerticesMap = new Map();
  for (let i = 0; i < vertexCount; i++) {
    const idx = i * 3;
    const key = `${positions[idx].toFixed(5)},${positions[idx + 1].toFixed(5)},${positions[idx + 2].toFixed(5)}`;
    if (!uniqueVerticesMap.has(key)) {
      uniqueVerticesMap.set(key, new THREE.Vector3(positions[idx], positions[idx + 1], positions[idx + 2]));
    }
  }
  const uniqueVertices = Array.from(uniqueVerticesMap.values());

  // Prepare result object
  const verticesPerTooth = {};

  // For each tooth, find vertices within radius of tooth center
  for (const [toothId, toothData] of Object.entries(dentalMap)) {
    if (!toothData.medial || !toothData.distal) continue;

    // Calculate tooth center as midpoint between medial and distal
    const toothCenter = new THREE.Vector3().lerpVectors(toothData.medial, toothData.distal, 0.5);

    // Collect vertices within radius of projectedCenter
    const verticesInRadius = uniqueVertices.filter(v => v.distanceTo(toothCenter) <= radius);

    verticesPerTooth[toothId] = verticesInRadius;
    toothData.verticesInRadius = verticesInRadius;
    toothData.dbScan = dbscan(verticesInRadius);
  }

  return verticesPerTooth;
}

/**
 * Main function to get per-tooth vertex clouds from mesh edges for contour processing
 * @param {THREE.Mesh} mesh - Input mesh
 * @param {Object} dentalMap - Dental mapping data
 * @param {Object} options - Options object
 * @param {number} [options.filterAngle=5] - Angle threshold for internal edges
 * @param {number} [options.radius=10] - Radius around tooth center to collect vertices
 * @returns {Object} Object with toothId keys and array of THREE.Vector3 vertices as values
 */
export function extractToothCrownVertices(mesh, dentalMap, options = {}) {
  const {
    filterAngle = 8,
    radius = 8,
  } = options;

  // Get filtered edges geometry
  const filteredEdgesGeometry = getFilteredEdgesGeometry(mesh, filterAngle);

  // Get vertices per tooth
  const verticesPerTooth = getVerticesPerTooth(filteredEdgesGeometry, dentalMap, radius);

  return verticesPerTooth;
}

/**
 * Triggers download of dentalMap object as a JSON file named dentalMap.js in the browser
 * @param {Object} dentalMap - The dentalMap object to save
 * @param {string} [fileName='dentalMap.js'] - The file name for the downloaded file
 */
export function downloadDentalMapAsFile(dentalMap, fileName = 'dentalMap.js') {
  const jsonData = JSON.stringify(dentalMap, null, 2);
  const blob = new Blob([jsonData], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.style.display = 'none';

  document.body.appendChild(a);
  a.click();

  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function dbscan(pontos, epsilon = 0.2, minPontos = 3) {
    const clusters = [];
    const ruido = new Set();
    const visitados = new Set();
    const areasCluster = [];

    function calcularDistancia(p1, p2) {
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        const dz = p1.z - p2.z;
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    function estaEmAreaDeCluster(centroide) {
        return areasCluster.some(area =>
            calcularDistancia(centroide, area.centroide) <= epsilon
        );
    }

    for (let i = 0; i < pontos.length; i++) {
        if (visitados.has(i)) continue;

        const ponto = pontos[i];

        // Coleta de pontos dentro do raio epsilon
        const vizinhos = [];
        for (let j = 0; j < pontos.length; j++) {
            if (calcularDistancia(ponto, pontos[j]) <= epsilon) {
                vizinhos.push(pontos[j]);
            }
        }

        if (vizinhos.length < minPontos) {
            ruido.add(i);
            continue;
        }

        // Calcular centroide dos vizinhos
        const soma = vizinhos.reduce((acc, p) => {
            acc.x += p.x;
            acc.y += p.y;
            acc.z += p.z;
            return acc;
        }, { x: 0, y: 0, z: 0 });

        const centroide = {
            x: soma.x / vizinhos.length,
            y: soma.y / vizinhos.length,
            z: soma.z / vizinhos.length
        };

        // Verificar se o centroide está em área já ocupada
        if (estaEmAreaDeCluster(centroide)) {
            // Marcar os vizinhos como visitados, mas não os adiciona a um cluster
            for (const p of vizinhos) {
                visitados.add(pontos.indexOf(p));
            }
            continue;
        }

        // Cluster válido
        const cluster = {
            centroide: centroide,
            pontos: vizinhos
        };

        for (const p of vizinhos) {
            visitados.add(pontos.indexOf(p));
        }

        areasCluster.push({
            centroide: centroide,
            raio: 2 * epsilon
        });

        clusters.push(cluster);
    }

    // Identificar ruído não processado
    for (let i = 0; i < pontos.length; i++) {
        if (!visitados.has(i)) {
            ruido.add(i);
        }
    }

    return {
        clusters: clusters,
        noise: Array.from(ruido).map(i => pontos[i])
    };
}
